import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { removeObjectEmptyProps } from '@belong/type-utils';
import type { PageServerSidePropsFunction, ISupportServerAppProps } from 'src/core/types';
import { getBreadcrumbs } from 'src/models/breadcrumbs';
import { getServerSideAppProps } from './getServerSideAppProps';

export function withServerSideAppProps(
  getPageServerSideProps: PageServerSidePropsFunction,
  requireAuthentication = false
) {
  return async (context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<ISupportServerAppProps>> => {
    const appPropsResult = await getServerSideAppProps(context, requireAuthentication);

    if ('redirect' in appPropsResult) {
      return { redirect: appPropsResult.redirect };
    }
    if ('notFound' in appPropsResult) {
      return { notFound: true };
    }

    const appProps = await appPropsResult.props;

    const { pageUrl, config } = await appProps;
    const {
      categories,
      settings: { breadcrumbsHomeLink }
    } = config;

    const pageProps = await getPageServerSideProps(context, appProps);

    // If getPageServerSideProps fails to load page data, return notFound and triggers 404
    if ('notFound' in pageProps) {
      return { notFound: true };
    }

    const { data, meta } = pageProps;

    // breadcrumbs need to be generated using app and page data
    // because if the page is an article, we need to prepend the article url to the breadcrumbs
    const breadcrumbs = getBreadcrumbs({ path: pageUrl, categories, homeLink: breadcrumbsHomeLink, pageContent: data });
    const combinedProps = removeObjectEmptyProps<ISupportServerAppProps>({
      ...appProps,
      data,
      meta,
      breadcrumbs
    });

    return {
      props: combinedProps
    };
  };
}
